import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaCrown } from 'react-icons/fa';
import './scss/proSignupPage.scss';
import logo from './images/logo-long.svg';
import api from './services/apiService';
import stripeService from './services/stripeService';

const ProSignupPage = () => {
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [phoneError, setPhoneError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if this is the parent page
    const isParent = location.pathname.includes('/parent');
    const userType = isParent ? 'parent' : 'player';

    // Copy based on user type
    const pageCopy = isParent ? {
        headline: "Celebrate Your Child's Football Journey",
        membershipLabel: "NEW YEAR SPECIAL OFFER",
        description: "Get unlimited access to your child's complete football story. See every match, every achievement, and track their progress over time.",
        features: [
            "Match Reports - access all your child's past matches, not just the most recent",
            "Full achievement collection - view every achievement they've earned, not just the latest",
            "See how they compare - view the full leaderboard to see where your child ranks against teammates"
        ],
        cta: 'Join FirstWhistle'
    } : {
        headline: "Unlock Your Complete Football Story",
        membershipLabel: "NEW YEAR SPECIAL OFFER",
        description: "Go Pro and get unlimited access to your entire football journey. See every match, every achievement, and track your progress.",
        features: [
            "Compare your stats against teammates - see how you measure up in goals, assists, and more",
            "Full achievement collection - view every achievement you've earned, not just the latest",
            "Full leaderboard access - see where you rank and how you compare to teammates"
        ],
        cta: 'Start My Pro Journey'
    };

    const validatePhoneNumber = (phone) => {
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        // Check if it's a valid UK phone number (10-11 digits, may start with 0 or country code)
        if (cleaned.length < 10 || cleaned.length > 11) {
            return false;
        }
        return true;
    };

    const handleGetStarted = async () => {
        // Validate phone number
        if (!phoneNumber || !phoneNumber.trim()) {
            setPhoneError('Please enter your phone number');
            return;
        }

        if (!validatePhoneNumber(phoneNumber)) {
            setPhoneError('Please enter a valid UK phone number');
            return;
        }

        setPhoneError('');
        setIsProcessingPayment(true);
        setPaymentError(null);

        try {
            // Clean phone number (remove spaces, dashes, etc.)
            const cleanedPhone = phoneNumber.replace(/\D/g, '');
            
            // Check if user exists with this phone number
            const checkUserResponse = await api.post('/check_user_exists.php', {
                mobile: cleanedPhone,
                userType: userType
            });
            
            if (checkUserResponse.data.exists) {
                // Check if user is already Pro
                if (checkUserResponse.data.isPro) {
                    setPaymentError('You already have an active Pro membership.');
                    setIsProcessingPayment(false);
                    return;
                }
                
                // User exists - get their player/parent ID and team info
                const userData = checkUserResponse.data;
                // Use API URL for thank you page (works for both web and Capacitor apps)
                const apiUrl = process.env.REACT_APP_API_URL || 'https://football.pebbledesigns.co.uk/api';
                const baseUrl = `${apiUrl}/payment-thank-you.php`;
                const paymentType = billingCycle === 'monthly' ? 'pro_monthly' : 'pro_annual';
                
                // Use full pricing: £4 monthly or £52 annual
                const amount = billingCycle === 'monthly' ? 4.00 : 52.00;
                
                let checkoutOptions;
                
                if (isParent) {
                    // For parents - they don't need a team association
                    checkoutOptions = {
                        parentId: userData.userId,
                        teamId: 1, // Use a placeholder team ID for parents
                        amount: amount,
                        paymentType,
                        isSubscription: billingCycle === 'monthly',
                        subscriberType: 'parent',
                        successUrl: `${baseUrl}?session_id={CHECKOUT_SESSION_ID}&type=${paymentType}&phone=${encodeURIComponent(cleanedPhone)}&userType=${userType}`,
                        cancelUrl: `${window.location.origin}/pro-signup${isParent ? '/parent' : ''}`
                    };
                } else {
                    // For players - must have a team to upgrade
                    const teamId = userData.existingTeams?.[0]?.team_id;
                    
                    if (!teamId) {
                        setPaymentError('You need to be part of a team to upgrade to Pro. Please join a team first.');
                        setIsProcessingPayment(false);
                        return;
                    }
                    
                    checkoutOptions = {
                        playerId: userData.userId,
                        teamId: teamId,
                        amount: amount,
                        paymentType,
                        isSubscription: billingCycle === 'monthly',
                        subscriberType: 'player',
                        successUrl: `${baseUrl}?session_id={CHECKOUT_SESSION_ID}&type=${paymentType}&phone=${encodeURIComponent(cleanedPhone)}&userType=${userType}`,
                        cancelUrl: `${window.location.origin}/pro-signup${isParent ? '/parent' : ''}`
                    };
                }
                
                // Redirect to Stripe checkout
                await stripeService.createAndOpenCheckout(checkoutOptions);
            } else {
                // User doesn't exist - redirect to registration with phone pre-filled
                sessionStorage.setItem('pro_signup_phone', cleanedPhone);
                sessionStorage.setItem('pro_signup_billing', billingCycle);
                navigate(`/?phone=${cleanedPhone}&pro_signup=true&register=true`);
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            setPaymentError(error.message || 'Unable to process payment. Please try again.');
            setIsProcessingPayment(false);
        }
    };

    return (
        <div className="pro-signup-page">
            <div className="pro-signup-container">
                <div className="logo-container">
                    <img src={logo} alt="FirstWhistle" className="logo" />
                </div>

                <div className="content-wrapper">
                    <FaCrown className="crown-icon" />
                    <h1>{pageCopy.headline}</h1>
                    {pageCopy.membershipLabel && (
                        <p className="membership-label">{pageCopy.membershipLabel}</p>
                    )}
                    <p className="description">
                        {pageCopy.description}
                    </p>

                    <ul className="features-list">
                        {pageCopy.features.map((feature, index) => (
                            <li key={index}>{feature}</li>
                        ))}
                    </ul>

                    <div className="pricing-toggle">
                        <button 
                            className={`toggle-button ${billingCycle === 'monthly' ? 'active' : ''}`}
                            onClick={() => setBillingCycle('monthly')}
                        >
                            Monthly
                        </button>
                        <button 
                            className={`toggle-button annual ${billingCycle === 'annual' ? 'active' : ''}`}
                            onClick={() => setBillingCycle('annual')}
                        >
                            Annual
                            <span className="save-label">Save £12</span>
                        </button>
                    </div>

                    <div className="pricing-info">
                        {billingCycle === 'monthly' ? (
                            <div className="price-details">
                                <div className="price-container">
                                    <span className="price">
                                        <span className="currency-symbol">£</span>4
                                    </span>
                                </div>
                                <span className="period">per month</span>
                            </div>
                        ) : (
                            <div className="price-details">
                                <div className="price-container">
                                    <span className="price">
                                        <span className="currency-symbol">£</span>52
                                    </span>
                                </div>
                                <span className="period">per year - just £1 per week</span>
                            </div>
                        )}
                    </div>

                    <div className="phone-input-section">
                        <label htmlFor="phone-number" className="phone-label">
                            Enter your phone number to get started
                        </label>
                        <input
                            id="phone-number"
                            type="tel"
                            className={`phone-input ${phoneError ? 'error' : ''}`}
                            placeholder="07123 456789"
                            value={phoneNumber}
                            onChange={(e) => {
                                setPhoneNumber(e.target.value);
                                setPhoneError('');
                            }}
                            disabled={isProcessingPayment}
                        />
                        {phoneError && (
                            <p className="error-message">{phoneError}</p>
                        )}
                    </div>

                    {paymentError && (
                        <div className="payment-error-message">
                            <p>{paymentError}</p>
                        </div>
                    )}

                    <button 
                        className="get-started-button" 
                        onClick={handleGetStarted}
                        disabled={isProcessingPayment}
                    >
                        {isProcessingPayment ? 'Processing...' : pageCopy.cta}
                    </button>

                    {billingCycle === 'monthly' && (
                        <div className="billing-info">
                            <span>
                                <span className="billing-note">Charged monthly.</span>{' '}
                                <span className="cancel-text">Cancel anytime</span>
                            </span>
                            <span className="payment-note">Payment completed on our website</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProSignupPage;

